'use client'

import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { IoCheckmarkCircle } from 'react-icons/io5'

import { cn } from '@/lib/utils'
import { PublicPricingPlanCardDto } from '@/server/public-api/types'
import { buildLocalePath } from '@/utils'
import { getPlanPriceDisplay } from '@/utils/price'

type PlanProps = {
  plan: PublicPricingPlanCardDto
  timeWindowName?: string
  className?: string
}

const PlanCard = ({ plan, className }: PlanProps) => {
  const { i18n, t } = useTranslation()
  const price = getPlanPriceDisplay(plan, i18n.language)
  return (
    <article className='flex h-full flex-col rounded-[40px] border-2 border-[#9747FF] bg-white text-center'>
      <div className={cn('h6 rounded-full bg-[#004A49] py-2 font-bold text-white text-4xl', className)}>
        {plan.name}
      </div>
      {/* A plan with no price shows no figure at all (never a fake 0). */}
      {price.final ? (
        <div>
          <p className='mt-4 flex items-center justify-center gap-1 rounded-3xl border border-[#9747FF] bg-black py-4 text-2xl font-semibold text-white'>
            {/* `<bdi>` isolates each amount so the Arabic paragraph cannot reorder the
                currency and digits. suppressHydrationWarning: Node's ICU and the
                visitor's browser may spell a currency differently (e.g. "US$");
                the server's rendering is kept and is equally correct. */}
            {price.original ? (
              <bdi suppressHydrationWarning className='me-3 line-through'>
                {price.original}
              </bdi>
            ) : null}
            <bdi suppressHydrationWarning>{price.final}</bdi>
            <span className='text-sm font-normal opacity-80'>/ {t('pricing.timeframe.month')}</span>
          </p>
        </div>
      ) : null}
      <ul className='space-y-8 px-2 py-6 text-start text-lg font-medium capitalize'>
        {plan.features.map((feature, index) => (
          <li key={index} className='flex items-center gap-2'>
            <IoCheckmarkCircle className='size-6 fill-[#004A49]' />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      <Link
        href={buildLocalePath(i18n.language, '/register')}
        className={cn(
          ' inline-block rounded-[20px] bg-[#004A49] p-3 text-lg font-medium text-white w-full hover:bg-green-700 mt-auto ',
          className
        )}
      >
        {t('registerNow')}
      </Link>
    </article>
  )
}

export default PlanCard
